import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ShieldCheck, ShieldAlert, Info } from "lucide-react";
import { article36Api } from "../../services/article36Api";
import { StatusBadge } from "../../components/StatusBadge";
import { EagleState } from "../../components/EagleState";
import { useLocalization } from "../../hooks/useLocalization";
import { formatApiErrorDetail } from "../../lib/api";
import { formatDate } from "../../lib/date";
import { Article36AgreementForm } from "../../components/Article36AgreementForm";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { useRouteRefresh } from "../../hooks/useRouteRefresh";

export default function Article36DashboardPage() {
  const { t } = useLocalization();
  const [dashboard, setDashboard] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const load = () => {
    article36Api.dashboard()
      .then((r) => { setDashboard(r.data); setAccessDenied(false); })
      .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); });
    article36Api.list().then((r) => setAgreements(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useRouteRefresh(load);

  const handleRenew = async (id) => {
    try {
      await article36Api.renew(id);
      toast.success(t("article36.createSuccess"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sorted = [...agreements].sort((a, b) => (b.effective_date || "").localeCompare(a.effective_date || ""));
  const current = sorted.find((a) => a.status === "ACTIVE") || sorted.find((a) => a.status !== "REPLACED") || null;
  const historical = sorted.filter((a) => a.id !== current?.id);

  if (accessDenied) return <AccessDenied resourceLabel={t("article36.title")} />;

  return (
    <div className="max-w-6xl" data-testid="article36-dashboard-page">
      <PageHelpBanner pageKey="article36" title="How to use this page">
        <p>{t("article36.subtitle")}</p>
        <p>Create at most <strong>one current agreement</strong> for this workplace and effective period. Do not create a new agreement per employee - use the "Covered employee groups" field on the form to describe who the single agreement applies to.</p>
      </PageHelpBanner>

      <div className="flex items-center justify-between mb-1">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("article36.title")}</h2>
        {!current && (
          <button
            data-testid="new-article36-agreement-button"
            onClick={() => setShowForm((s) => !s)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all"
          >
            <Plus size={16} /> {t("article36.newAgreement")}
          </button>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-6">{t("article36.subtitle")}</p>

      {dashboard && (
        <div
          data-testid="article36-gate-banner"
          className={`rounded-xl border p-4 mb-6 flex items-center gap-3 ${
            dashboard.gate_status === "ACTIVE" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {dashboard.gate_status === "ACTIVE" ? <ShieldCheck size={20} /> : <ShieldAlert size={20} />}
          <div>
            <p className="text-sm font-semibold">{dashboard.gate_status === "ACTIVE" ? t("article36.gateActive") : t("article36.gateInactive")}</p>
            {dashboard.days_remaining != null && (
              <p className="text-xs" data-testid="article36-days-remaining">{dashboard.days_remaining} {t("article36.daysRemaining")}</p>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <Article36AgreementForm
          onCreated={() => { setShowForm(false); load(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {current && (
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Current Workplace Agreement</p>
          <div className="bg-white rounded-xl border-2 border-slate-900 p-5 flex items-center justify-between" data-testid={`article36-agreement-item-${current.id}`}>
            <div>
              <Link to={`/management/article36/${current.id}`} className="font-bold text-gray-900 hover:underline" data-testid={`article36-agreement-link-${current.id}`}>
                {current.business_name}
              </Link>
              <p className="text-xs text-gray-500">{formatDate(current.effective_date)} → {formatDate(current.expiration_date)}</p>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status={current.status} />
              {["EXPIRING", "EXPIRED", "ACTIVE"].includes(current.status) && (
                <button data-testid={`article36-renew-button-${current.id}`} onClick={() => handleRenew(current.id)} className="text-xs font-semibold text-blue-600 hover:underline">
                  {t("article36.renew")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!current && (
        <EagleState variant="empty" title={t("article36.noAgreements")} message={t("article36.subtitle")} testId="article36-empty" />
      )}

      {historical.length > 0 && (
        <div className="mt-6">
          <button onClick={() => setShowHistory((s) => !s)} data-testid="article36-history-toggle" className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-600">
            {showHistory ? "Hide" : "Show"} historical & other records ({historical.length})
          </button>
          {showHistory && (
            <div className="space-y-3 mt-3">
              {historical.map((a) => (
                <div key={a.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between opacity-80" data-testid={`article36-agreement-item-${a.id}`}>
                  <div>
                    <Link to={`/management/article36/${a.id}`} className="font-semibold text-gray-700 text-sm hover:underline" data-testid={`article36-agreement-link-${a.id}`}>
                      {a.business_name}
                    </Link>
                    <p className="text-xs text-gray-500">{formatDate(a.effective_date)} → {formatDate(a.expiration_date)}</p>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-2" data-testid="article36-example-warning">
        <Info size={16} className="text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-amber-900">{t("article36.exampleWarningTitle")}</p>
          <p className="text-xs text-amber-800">{t("article36.exampleWarningBody")}</p>
        </div>
      </div>
    </div>
  );
}
